import { useEffect, useState } from 'react';
import { Drawer, DrawerSection } from '../../billing/Drawer';
import { useCreateTeam, useDeleteTeam, useUpdateTeam } from '../../../hooks/useTeams';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { Role, RoleScope } from '../../../services/roles';
import { DeleteTeamConfirmModal } from './DeleteTeamConfirmModal';
import { TeamColorTile } from './TeamColorTile';
import { TeamMembersSection } from './TeamMembersSection';
import { TeamScopeTag } from './TeamScopeTag';
import { isAdminTeamName, TEAM_NAME_MAX_LENGTH, validateTeamName } from './teamValidation';

type TeamDrawerProps =
    | { mode: 'create'; onClose: () => void }
    | {
          mode: 'edit';
          /** `null` while the teams list is still loading, or the team was not found (e.g. deleted elsewhere). */
          team: Role | null;
          isLoadingTeams: boolean;
          onClose: () => void;
      };

const SCOPE_OPTIONS: { value: RoleScope; label: string }[] = [
    { value: 'odo', label: 'ODO' },
    { value: 'practice', label: 'Practice' }
];

const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed';

function LoadingDrawer({ onClose }: { onClose: () => void }) {
    return (
        <Drawer title="Team" onClose={onClose}>
            <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                <span className="text-sm">Loading team…</span>
            </div>
        </Drawer>
    );
}

function NotFoundDrawer({ onClose }: { onClose: () => void }) {
    return (
        <Drawer title="Team" onClose={onClose}>
            <p className="text-sm text-gray-500 py-16 text-center">
                Couldn't find this team. It may have been deleted.
            </p>
        </Drawer>
    );
}

// Create + edit for a Team (a `roles` row). Deep-linked at `/access/teams/:teamId`
// like AccessUsersList's UserDrawer; the edit-mode `team` is sourced from the
// already-loaded list (there's no single-team GET), same pattern as
// AccessUsersList passing `lastActive` in from its row.
export function TeamDrawer(props: TeamDrawerProps) {
    const { onClose } = props;
    const isCreate = props.mode === 'create';
    const team = props.mode === 'edit' ? props.team : null;

    const { hasPermission } = useMyPermissions();
    const canManageTeams = hasPermission('iam.update');
    const { showToast } = useToast();
    const { mutateAsync: create, isPending: isCreating } = useCreateTeam();
    const { mutateAsync: update, isPending: isUpdating } = useUpdateTeam();
    const { mutateAsync: remove, isPending: isDeleting } = useDeleteTeam();

    const [name, setName] = useState(team?.name ?? '');
    const [scope, setScope] = useState<RoleScope>(team?.scope ?? 'odo');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // The team row arrives asynchronously (loaded list, not this drawer's own
    // fetch) - seed the form once it lands rather than only on first render.
    useEffect(() => {
        if (team) {
            setName(team.name);
            setScope(team.scope);
        }
    }, [team]);

    if (props.mode === 'edit' && !props.team) {
        return props.isLoadingTeams ? (
            <LoadingDrawer onClose={onClose} />
        ) : (
            <NotFoundDrawer onClose={onClose} />
        );
    }

    const isAdminTeam = Boolean(team && isAdminTeamName(team.name));
    const nameError = validateTeamName(name);
    const showNameError = name.trim().length > 0 && Boolean(nameError);
    const isDirty = isCreate || (team ? name.trim() !== team.name : false);
    const isSaving = isCreating || isUpdating;
    const nameDisabled = isAdminTeam || !canManageTeams;
    const saveDisabled = isSaving || Boolean(nameError) || isAdminTeam || (!isCreate && !isDirty);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (nameError) {
            showToast(nameError, 'error');
            return;
        }

        try {
            if (isCreate) {
                await create({ name: name.trim(), scope });
                showToast('Team created', 'success');
            } else if (team) {
                await update({ teamId: team.id, input: { name: name.trim() } });
                showToast('Team updated', 'success');
            }
            onClose();
        } catch (error) {
            showToast(
                apiErrorMessage(
                    error,
                    isCreate ? 'Failed to create team' : 'Failed to update team'
                ),
                'error'
            );
        }
    };

    const handleDelete = async () => {
        if (!team) return;
        try {
            await remove(team.id);
            showToast('Team deleted', 'success');
            setShowDeleteConfirm(false);
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to delete team'), 'error');
        }
    };

    return (
        <>
            <Drawer
                leading={team ? <TeamColorTile name={team.name} size="md" /> : undefined}
                title={isCreate ? 'New team' : (team?.name ?? '')}
                onClose={onClose}
                closeDisabled={isSaving || showDeleteConfirm}
            >
                <DrawerSection title="Details">
                    <form
                        onSubmit={(e) => {
                            void handleSubmit(e);
                        }}
                        className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
                    >
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                Team name *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={TEAM_NAME_MAX_LENGTH}
                                disabled={nameDisabled}
                                placeholder="e.g. Clinical Ops"
                                className={`${INPUT_CLASS} ${showNameError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                            />
                            {showNameError ? (
                                <p className="mt-1.5 text-[11px] text-red-600">{nameError}</p>
                            ) : (
                                <p className="mt-1.5 text-[11px] text-gray-400">
                                    Letters, numbers, spaces, and hyphens only - up to{' '}
                                    {TEAM_NAME_MAX_LENGTH} characters.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                Scope *
                            </label>
                            {isCreate ? (
                                <div className="grid grid-cols-2 gap-2">
                                    {SCOPE_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => setScope(option.value)}
                                            className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                                scope === option.value
                                                    ? 'border-gray-800 bg-gray-800 text-white'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                team && (
                                    <div className="flex items-center gap-2">
                                        <TeamScopeTag scope={team.scope} />
                                        <span className="text-[11px] text-gray-400">
                                            Can't be changed after a team is created.
                                        </span>
                                    </div>
                                )
                            )}
                        </div>

                        {isAdminTeam && (
                            <p className="text-[11px] text-gray-500 bg-white border border-gray-200 rounded-md px-3 py-2">
                                The Admin team cannot be renamed or deleted.
                            </p>
                        )}

                        <div className="flex justify-between items-center gap-3 pt-1">
                            <div>
                                {!isCreate && team && canManageTeams && !isAdminTeam && (
                                    <button
                                        type="button"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="bg-white text-red-800 border border-red-800 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                                    >
                                        Delete team
                                    </button>
                                )}
                            </div>
                            {canManageTeams && (
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isSaving}
                                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saveDisabled}
                                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSaving
                                            ? 'Saving…'
                                            : isCreate
                                              ? 'Create team'
                                              : 'Save changes'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>
                </DrawerSection>

                {!isCreate && team && <TeamMembersSection team={team} canManage={canManageTeams} />}
            </Drawer>

            {showDeleteConfirm && team && (
                <DeleteTeamConfirmModal
                    team={team}
                    isDeleting={isDeleting}
                    onConfirm={() => {
                        void handleDelete();
                    }}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            )}
        </>
    );
}
